import React, { useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Copy, FolderInput, IndentIncrease, Play, RotateCcw, Star, StarOff, Trash2 } from 'lucide-react'
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuSeparator,
} from '@/src/renderer/components/ui/context-menu'
import {
  TaskStartIcon,
  TaskDeadlineIcon,
  TaskAttentionIcon,
  DurationIcon,
  TagIcon,
  MotivationIcon,
  RecurringIcon,
} from '@/src/renderer/components/icons'
import { DormantIcon } from '@/src/renderer/components/icons/DormantIcon'
import { AreaIcon } from '@/src/renderer/components/icons/AreaIcon'
import { ReactivateIcon } from '@/src/renderer/components/icons/ReactivateIcon'
import { TaskMenuTargetContent } from './TaskMenuTargetContent'
import { taskMenuMarks } from './taskMenuMarks'
import { TASK_MENU_SHORTCUTS } from './taskMenuShortcuts'
import { useShortcutText } from '@/src/renderer/hooks/useShortcutText'
import type { TaskMenuProps } from './TaskMenu.types'
import { NestPicker } from '../NestPicker'
import { NestMenuContext } from '../../utils/nestMenu'
import { MoveMenuContext } from '../../utils/moveMenu'
import { MultiSelectionContext } from '../../utils/multiSelection'
import { MenuAction, MenuSub, RecurrenceLockedRow, ReminderMenuSub } from './TaskMenuRows'
import { useTaskDetailsOpener } from '../../hooks/TaskDetailsOpener'
import { useTaskMenuSubs } from './useTaskMenuSubs'

/**
 * Das **Aufgaben-Menü** (CONTEXT.md) — was der Rechtsklick auf eine Aufgabe
 * öffnet.
 *
 * Es löst die frühere Icon-Leiste ab, die auf `@floating-ui/react` saß und
 * Backdrop, Lage und Einblendung selbst mitbrachte. Radix bringt das alles mit;
 * hier steht nur noch, WAS in den Zeilen steht.
 *
 * **Vier Gruppen, drei Striche** — es passt nicht alles zu allem: Zeit (Wann,
 * Dauer, Deadline, Wiederkehrend), Einordnung (Aufmerksamkeit, Tags,
 * Belohnung), Aufgabe (Ruhen, Duplizieren) und Löschen allein am Ende. Die
 * Gruppen tragen keine sichtbaren Überschriften; sie sagten nichts, was die
 * Striche nicht schon sagen, und machten aus zehn Zeilen vierzehn.
 *
 * **Die Kürzel stehen nicht hier**, sondern in `taskMenuShortcuts.ts` — sie
 * gelten auch ohne offenes Menü, und zwei getippte Listen liefen auseinander.
 *
 * @example
 * <TaskMenu task={task} marks={marks} …>
 *   <div className="task-entry">…</div>
 * </TaskMenu>
 */
export const TaskMenu: React.FC<TaskMenuProps> = ({
  task, marks,
  onStartChange, onDurationChange, onDeadlineChange, onAttentionChange, onTagsChange, onRewardChange,
  onRecurrenceChange,
  onFocusStart, multiSelected, onPause, onReactivate, onDuplicate, onDelete, onFavoriteChange,
  kind = 'task', onReopen, onShowInProject,
  disabled = false,
  remindersMarked = false,
  children,
}) => {
  const { t } = useTranslation()
  /*
    In einer Liste kommt die Mehrfachauswahl nicht als Prop (#303), sondern als Frage — gestellt beim Öffnen:
    Der Wechsel des Zustands rendert das Menü neu, und erst dann zählt die Antwort.
  */
  const isMultiSelected = useContext(MultiSelectionContext)
  const [, setMenuOpen] = useState(false)
  const multi = multiSelected || (isMultiSelected?.() ?? false)
  // ⌘D unter Windows als Strg+D bzw. Ctrl+D (gemeldet 2026-09-24).
  const shortcutText = useShortcutText()

  // „Verschieben …“ (#257) — nur, wo die Seite den Weg bereitstellt.
  const moveMenu = useContext(MoveMenuContext)
  // Eine Unteraufgabe wandert mit ihrer Aufgabe und hat für sich „Umhängen“ (CONTEXT.md) — wie im Menü „Aufgaben“.
  const canMove = moveMenu && task.parentTitle == null ? moveMenu : null
  const { openSub, setOpenSub, close, handleContentKeyDown } = useTaskMenuSubs(
    !!task.parentRecurring,
    canMove ? () => canMove.open(task.id) : undefined,
  )

  /*
    **Favorit** (#210): Eine Zeile, zwei Stellungen — wie „Ruhen lassen“. Bei gemischter Auswahl gewinnt
    „Hinzufügen“; wer schon Favorit ist, behält seinen Platz (`useContextMenuBadgeUpdate`).
  */
  const favorite = marks?.favorite ?? !!task.favorite
  const favoriteRow = onFavoriteChange && (
    <MenuAction
      icon={favorite ? <StarOff size={16} strokeWidth={2} /> : <Star size={16} strokeWidth={2} />}
      label={t(favorite ? 'favorites.remove' : 'favorites.add')}
      shortcut={shortcutText(TASK_MENU_SHORTCUTS.favorite)}
      onSelect={() => onFavoriteChange(!favorite)}
    />
  )
  // „Einhängen in …“ (#204) — kein Kürzel-Ziel wie die sieben oben, deshalb eigener Zustand.
  const [nestOpen, setNestOpen] = useState(false)
  const nestMenu = useContext(NestMenuContext)
  const detailsOpener = useTaskDetailsOpener()

  const handlers = useMemo(
    () => ({ onStartChange, onDurationChange, onDeadlineChange, onAttentionChange, onTagsChange, onRewardChange, onRecurrenceChange }),
    [onStartChange, onDurationChange, onDeadlineChange, onAttentionChange, onTagsChange, onRewardChange, onRecurrenceChange]
  )

  // Ohne durchgereichte Auswahl zählt die angeklickte Aufgabe allein.
  const shown = marks ?? taskMenuMarks([task])

  const sub = (target: Parameters<typeof TaskMenuTargetContent>[0]['target']) => (
    <TaskMenuTargetContent
      target={target}
      task={task}
      handlers={handlers}
      // Dieselbe Richtung wie die eigene Zeile weiter unten: Ruht die Aufgabe
      // schon, heißt es dort „Aufwecken …" — und hier gibt es nichts zu legen.
      onRest={task.dormant ? undefined : onPause}
      // Entfernen folgt der Auswahl, auf die das Menü schreibt (#313).
      removable={shown.removable}
      onClose={close}
    />
  )

  /*
    **Das Menü auf Geschafft** — drei Zeilen (CONTEXT.md, Eintrag Geschafft).
    Kein Duplizieren: `cloneTaskFields` kopiert `status` und `completedAt`
    mit, die Kopie stünde abgehakt daneben. Dass die AUFGEKLAPPTE Karte die
    sechs Einordnungen trotzdem anbietet, ist Absicht — ein Menü ist eine
    Auswahl der häufigen Handlungen, kein Vertrag über das Mögliche.
  */
  if (kind === 'done') {
    return (
      <ContextMenu>
        <ContextMenuTrigger asChild disabled={disabled}>{children}</ContextMenuTrigger>
        <ContextMenuContent className="context-menu-content--space">
          <ContextMenuGroup>
            <MenuAction
              icon={<RotateCcw size={16} strokeWidth={2} />}
              label={t('taskItem.menu.reopen')}
              onSelect={() => onReopen?.()}
            />
            <MenuAction
              icon={<AreaIcon size={16} />}
              label={t('taskItem.menu.showInProject')}
              shortcut={shortcutText(TASK_MENU_SHORTCUTS.showInProject)}
              disabled={!onShowInProject}
              onSelect={() => onShowInProject?.()}
            />
            {favoriteRow}
          </ContextMenuGroup>

          <ContextMenuSeparator />

          <MenuAction
            danger
            icon={<Trash2 size={16} strokeWidth={2} />}
            label={t('taskItem.menu.delete')}
            shortcut={shortcutText(TASK_MENU_SHORTCUTS.delete)}
            onSelect={onDelete}
          />
        </ContextMenuContent>
      </ContextMenu>
    )
  }

  return (
    <ContextMenu onOpenChange={setMenuOpen}>
      {/*
        `disabled` statt weglassen: Der Kasten bleibt im Baum, auch wenn er
        gerade nichts tut. Wer ihn beim Aufklappen entfernt, verschiebt die
        Zeile im Baum — React baut sie dann neu, und die Auf-Animation beginnt
        auf frischem DOM. Siehe `TaskMenu.types.ts`.
      */}
      <ContextMenuTrigger asChild disabled={disabled}>{children}</ContextMenuTrigger>
      {/*
        **Der Ton des Lifespace** (`--space-950` / `-900` / `-50`), nach dem
        Vorbild der **Options-Leiste** des Canvas-Zettels. Die Werte stehen in
        `menu.scss`; hier steht nur, WER sie trägt.

        Die `ContextMenuSubContent` unten bekommt sie NICHT: Die Farbe endet an
        der Schwelle, die sechs Inhalte bleiben helle App-Popover — dieselbe
        Entscheidung wie an der Leiste, deren Farbwähler dunkel wird und deren
        Schriftmenü hell bleibt.
      */}
      <ContextMenuContent className="context-menu-content--space" onKeyDown={handleContentKeyDown}>
        {/* ── Zeit: alle drei sagen etwas über die Aufgabe in der ZEIT ── */}
        <ContextMenuGroup>
          <MenuSub
            icon={<TaskStartIcon size={16} />}
            label={t('taskItem.menu.when')}
            shortcut={shortcutText(TASK_MENU_SHORTCUTS.start)}
            open={openSub === 'start'}
            onOpenChange={(next) => setOpenSub(next ? 'start' : null)}
            marked={shown.start}
          >
            {sub('start')}
          </MenuSub>

          <MenuSub
            icon={<TaskDeadlineIcon size={16} />}
            label={t('taskItem.menu.deadline')}
            shortcut={shortcutText(TASK_MENU_SHORTCUTS.deadline)}
            open={openSub === 'deadline'}
            onOpenChange={(next) => setOpenSub(next ? 'deadline' : null)}
            marked={shown.deadline}
          >
            {sub('deadline')}
          </MenuSub>

          {/* Dauer unter der Deadline (gewünscht 2026-09-24): Start und Deadline stehen als die zwei Termine beieinander. */}
          <MenuSub
            icon={<DurationIcon size={16} />}
            label={t('taskItem.menu.duration')}
            shortcut={shortcutText(TASK_MENU_SHORTCUTS.duration)}
            open={openSub === 'duration'}
            onOpenChange={(next) => setOpenSub(next ? 'duration' : null)}
            marked={shown.duration}
          >
            {sub('duration')}
          </MenuSub>

          {/*
            Die **Wiederholung** (CONTEXT.md, Wiederkehrend) — in der Zeit-Gruppe,
            weil sie sagt, WANN die Aufgabe wieder ansteht. `⇧W` wie `⇧D`:
            dieselbe Art Angabe wie Wann, Umschalt nimmt die zweite.
          */}
          {/* Unter einer wiederkehrenden Aufgabe gilt deren Rhythmus (#205) — die Zeile sagt es. */}
          {task.parentRecurring ? <RecurrenceLockedRow /> : (
            <MenuSub
              icon={<RecurringIcon size={16} />}
              label={t('taskItem.menu.recurrence')}
              shortcut={shortcutText(TASK_MENU_SHORTCUTS.recurrence)}
              open={openSub === 'recurrence'}
              onOpenChange={(next) => setOpenSub(next ? 'recurrence' : null)}
              marked={shown.recurrence}
            >
              {sub('recurrence')}
            </MenuSub>
          )}

          <ReminderMenuSub
            taskId={multi ? undefined : task.id}
            marked={remindersMarked}
            open={openSub === 'reminders'}
            onOpenChange={(next) => setOpenSub(next ? 'reminders' : null)}
            onOpenDetails={detailsOpener ? () => detailsOpener.openReminders(task.id, undefined, 'at') : undefined}
          />
        </ContextMenuGroup>

        <ContextMenuSeparator />

        {/* ── Einordnung: was für eine Aufgabe ist das ── */}
        <ContextMenuGroup>
          <MenuSub
            icon={<TaskAttentionIcon size={16} />}
            label={t('taskItem.menu.attention')}
            shortcut={shortcutText(TASK_MENU_SHORTCUTS.attention)}
            open={openSub === 'attention'}
            onOpenChange={(next) => setOpenSub(next ? 'attention' : null)}
            marked={shown.attention}
          >
            {sub('attention')}
          </MenuSub>

          <MenuSub
            icon={<TagIcon size={16} />}
            label={t('taskItem.menu.tags')}
            shortcut={shortcutText(TASK_MENU_SHORTCUTS.tags)}
            open={openSub === 'tags'}
            onOpenChange={(next) => setOpenSub(next ? 'tags' : null)}
            marked={shown.tags}
          >
            {sub('tags')}
          </MenuSub>

          {/*
            Die **Belohnung** — hier zum ersten Mal angeschlossen. In der alten
            Icon-Leiste stand sie deaktiviert, mit dem Kommentar „shown disabled
            until it ships". Das stimmte längst nicht mehr: Das Dropdown ist
            fertig, es war nur nie verdrahtet.

            Sie trägt keinen Haken: Was sie setzt, entscheidet sich erst eine
            Ebene tiefer (Belohnung ODER Ziel), und ein Haken sagte nicht, was
            von beidem dransteht.
          */}
          <MenuSub
            icon={<MotivationIcon size={16} />}
            label={t('taskItem.menu.reward')}
            shortcut={shortcutText(TASK_MENU_SHORTCUTS.reward)}
            open={openSub === 'reward'}
            onOpenChange={(next) => setOpenSub(next ? 'reward' : null)}
            marked={false}
          >
            {sub('reward')}
          </MenuSub>
        </ContextMenuGroup>

        <ContextMenuSeparator />

        {/* ── Aufgabe: was die Aufgabe SELBST angeht ── */}
        <ContextMenuGroup>
          {/*
            **Play steht oben in dieser Gruppe** — es ist die Handlung, die man
            am häufigsten will, und der Rest der Gruppe (ruhen, duplizieren)
            schiebt die Aufgabe eher beiseite.

            Grau statt weg: Die Zeile sagt, was es hier gäbe — nur nicht für
            diese Aufgabe. An einer erledigten oder ruhenden gibt es nichts zu
            fokussieren — dort steht in der Zeile „Reaktivieren".

            **Und nicht bei mehreren** (Abnahme F-KS1): Ein Fokus gilt genau einer
            Aufgabe. Vorher startete die Zeile ihn an der zuletzt gewählten — der
            Play-Knopf AN der Zeile verschwindet in diesem Fall längst, das Menü
            zog nicht mit.
          */}
          <MenuAction
            icon={<Play size={16} strokeWidth={2} />}
            label={t('taskItem.menu.focus')}
            shortcut={shortcutText(TASK_MENU_SHORTCUTS.focus)}
            disabled={!onFocusStart || multi || task.completed || !!task.dormant}
            onSelect={() => onFocusStart?.()}
          />

          {/*
            Eine Zeile, zwei Stellungen. Bei gemischter Auswahl gewinnt die
            Handlung, die noch etwas zu tun hat: Solange eine Aufgabe wach ist,
            heißt sie „Ruhen lassen" (CONTEXT.md).
          */}
          {task.dormant ? (
            <MenuAction
              icon={<ReactivateIcon size={16} />}
              label={t('taskItem.menu.wake')}
              shortcut={shortcutText(TASK_MENU_SHORTCUTS.rest)}
              onSelect={onReactivate}
            />
          ) : (
            <MenuAction
              icon={<DormantIcon size={16} />}
              label={t('taskItem.menu.rest')}
              shortcut={shortcutText(TASK_MENU_SHORTCUTS.rest)}
              onSelect={onPause}
            />
          )}

          {favoriteRow}

          {/*
            **Im Projekt zeigen** (#257) — nur, wo die Seite es anbietet (Demnächst, Wiederkehrend,
            Ruhend) und die Aufgabe ein Projekt hat. Auf der Projektseite stünde man schon dort.
          */}
          {onShowInProject && (
            <MenuAction
              icon={<AreaIcon size={16} />}
              label={t('taskItem.menu.showInProject')}
              shortcut={shortcutText(TASK_MENU_SHORTCUTS.showInProject)}
              onSelect={() => onShowInProject()}
            />
          )}

          <MenuAction
            icon={<Copy size={16} strokeWidth={2} />}
            label={t('taskItem.menu.duplicate')}
            shortcut={shortcutText(TASK_MENU_SHORTCUTS.duplicate)}
            onSelect={onDuplicate}
          />

          {/*
            **Verschieben …** (#257, #263): kein Untermenü mehr — das Menü geht zu, und das Panel
            öffnet sich an der Zeile, wie mit ⇧⌘M (entschieden 05.10.2026, Prototyp Variante C).
          */}
          {canMove && (
            <MenuAction
              icon={<FolderInput size={16} strokeWidth={2} />}
              label={t('taskItem.menu.move')}
              shortcut={shortcutText(TASK_MENU_SHORTCUTS.move)}
              onSelect={() => canMove.open(task.id)}
            />
          )}

          {/*
            **Einhängen in …** (#204, Grill 9) — nur, wo die Liste es anbietet (Projekt, Kurz
            notiert). Eine wiederkehrende Aufgabe lässt sich einhängen, nur nicht in eine
            wiederkehrende — die stehen grau in der Auswahl (#205).
          */}
          {nestMenu && (
            <MenuSub
              icon={<IndentIncrease size={16} strokeWidth={2} />}
              label={t('taskList.nest.menu')}
              shortcut={shortcutText(TASK_MENU_SHORTCUTS.nest)}
              open={nestOpen}
              onOpenChange={setNestOpen}
              marked={false}
            >
              <NestPicker
                groups={nestOpen ? nestMenu.groupsFor(task.id) : []}
                onSelect={(parentId) => { nestMenu.nest(task.id, parentId); close() }}
                onClose={close}
              />
            </MenuSub>
          )}
        </ContextMenuGroup>

        <ContextMenuSeparator />

        {/* ── Die einzige Zeile, die etwas wegnimmt — allein und zuletzt. ── */}
        <MenuAction
          danger
          icon={<Trash2 size={16} strokeWidth={2} />}
          label={t('taskItem.menu.delete')}
          shortcut={shortcutText(TASK_MENU_SHORTCUTS.delete)}
          onSelect={onDelete}
        />
      </ContextMenuContent>
    </ContextMenu>
  )
}
